import { getCartCookie } from "@/utils/cart/getCartCookie";
import { useMarket } from "@app/_context";
import useEvents from "@app/_hooks/useEvents";
import type { Cart } from "@server/gql/graphql";
import { addToCart } from "@server/requests/checkout/addToCart";
import { createNewCart } from "@server/requests/checkout/createNewCart";
import type { IncomingItem } from "@server/utils/cart";
import {
  cartItemSkeleton,
  prepareIncomingItem,
  setCartCookie,
} from "@server/utils/cart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

type AddCartItem = <T extends boolean = false>(
  input: IncomingItem[],
  async?: T,
) => T extends true ? Promise<Cart> : void;

export default function useAddCartItem() {
  const { addToCart: track } = useEvents();
  const { serviceLocale: locale } = useMarket().state.market;
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isError, isPending, isSuccess } = useMutation({
    mutationFn: async (incomingItems: IncomingItem[]) => {
      const cartId = getCartCookie()?.value;
      const items = incomingItems.map(prepareIncomingItem);
      if (!cartId) {
        const newCart = await createNewCart({
          items,
          locale,
        });

        if (newCart instanceof Error) {
          throw new Error("Failed to create new cart");
        }

        void setCartCookie({
          id: newCart.id.toString(),
          token: newCart.token as string,
        });
        return newCart;
      }
      return addToCart({
        items,
        locale,
        cartId,
      });
    },
    onMutate: async (incomingItems) => {
      await queryClient.cancelQueries({ queryKey: ["cart"] });
      await queryClient.cancelQueries({ queryKey: ["checkout"] });
      await queryClient.cancelQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.cancelQueries({ queryKey: ["payment"] });

      const previousCart = queryClient.getQueryData<Cart>(["cart"]);

      queryClient.setQueryData<Cart>(["cart"], (currentCart) => {
        if (!currentCart) {
          return;
        }

        if (!incomingItems.length) {
          return currentCart;
        }

        const updatedItems = [...currentCart.items];
        for (const inputItem of incomingItems) {
          const { item, quantity: q } = inputItem;
          const index = currentCart.items.findIndex(
            (item) => item.partNo === item.partNo,
          );
          const itemExistsInCart = index > -1;
          if (itemExistsInCart && updatedItems[index]) {
            const updatedItem = updatedItems[index];
            const quantity = q + updatedItem.quantity;
            updatedItems[index] = {
              ...updatedItem,
              quantity,
              totalGrossAmount: updatedItem.price * quantity,
            };
          } else {
            updatedItems.push(
              cartItemSkeleton({
                partNo: item.id,
                quantity: q,
                price: item.price,
              }),
            );
          }
        }

        const newTotal = updatedItems.reduce(
          (sum, item) => sum + item.totalGrossAmount,
          0,
        );

        return {
          ...currentCart,
          items: updatedItems,
          totalGrossAmount: newTotal + currentCart.totalFreightInclVat,
        };
      });

      return { previousCart };
    },
    onSuccess: (data, incomingItems) => {
      track(incomingItems, data);
    },
    onError: (
      error,
      input,
      context: { previousCart: Cart | undefined } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData<Cart>(["cart"], context.previousCart);
      }
    },
    onSettled: async () => {
      void queryClient.invalidateQueries({ queryKey: ["cart"] });
      void queryClient.invalidateQueries({ queryKey: ["checkout"] });
      void queryClient.invalidateQueries({ queryKey: ["checkoutDelivery"] });
      await queryClient.invalidateQueries({ queryKey: ["payment"] });
    },
  });

  const addCartItem = useCallback(
    (input: IncomingItem[], async = false) => {
      if (async) return mutateAsync(input);
      return mutate(input);
    },
    [mutate, mutateAsync],
  );

  return {
    addCartItem: addCartItem as AddCartItem,
    isErrorAddCartItem: isError,
    isPendingAddCartItem: isPending,
    isSuccessAddCartItem: isSuccess,
  };
}
